import { S25String } from "./s25.string";
import { S25Number } from "./s25.number";
import { isDate, isUndefined } from "./index";

export function S25DateParse(date: number | string | Date | undefined | null): Date {
    date = S25DateParse.addPuncToDateTimeStr(date);
    if (date && typeof date === "string" && /^\d\d?:\d\d?(?::\d\d?)?\s*(?:am|pm)?$/i.test(date)) {
        return S25DateParse.time(date, true);
    }
    return S25DateParse.date(date);
}

export namespace S25DateParse {
    export function date(rawDate: number | string | Date): Date;
    export function date(rawDate: number | string | Date | undefined | null): Date | undefined;
    export function date(rawDate: number | string | Date) {
        // Invalid Date - return undefined for undefined
        if (!rawDate) {
            return undefined;
        }
        if (typeof rawDate === "number") return new Date(rawDate); // Assume number is epoch

        // Date object - clone and return
        if (Object.prototype.toString.call(rawDate) === "[object Date]") {
            return clone(rawDate);
        }

        const date = window.dayjs(rawDate).toDate() as Date;

        // if date is way back in the past and the rawDate did not include that year
        if (
            date.getFullYear() - new Date().getFullYear() <= -20 &&
            (rawDate as string)?.indexOf(date.getFullYear().toString()) === -1
        ) {
            if (date.getFullYear() >= 1900 && date.getFullYear() <= 1999) {
                // eg, parsed '2/8/51' as 1951, but we can always assume it is this century
                date.setFullYear(new Date().getFullYear() + 100);
            } else {
                // eg, 'march 2' parses as 2001-03-02, but we want it to be CURRENT_YEAR-03-02
                date.setFullYear(new Date().getFullYear());
            }
        }

        return date;
    }

    export function clone(date: Date | string): Date {
        if (typeof date === "string") {
            date = S25DateParse(date);
        }
        return window.dayjs(date).toDate();
    }

    export function addPuncToDateTimeStr<T>(dateStr: T): T {
        if (dateStr && typeof dateStr === "string" && dateStr.match(/^\d+$/)) {
            let newDateStr = "";

            if (dateStr.length >= 4) {
                newDateStr = dateStr.substr(0, 4);
            }

            if (dateStr.length >= 6) {
                newDateStr += "-" + dateStr.substr(4, 2);
            }

            if (dateStr.length >= 8) {
                newDateStr += "-" + dateStr.substr(6, 2);
            }

            if (dateStr.length >= 10) {
                newDateStr += "T" + dateStr.substr(8, 2);
            }

            if (dateStr.length >= 12) {
                newDateStr += ":" + dateStr.substr(10, 2);
            }

            if (dateStr.length >= 14) {
                newDateStr += ":" + dateStr.substr(12, 2);
            }

            return newDateStr as unknown as T;
        }

        return dateStr;
    }

    export function parseDropTZ(dateInput: any): Date {
        if (isUndefined(dateInput)) {
            // TODO: after turning on strictNullChecks, <any> will be unnecessary.
            //  without strictNullChecks, it thinks dateInput is number only :dizzy:
            return <any>dateInput;
        }

        let dateStr = "";
        if (isDate(dateInput)) {
            dateStr =
                dateInput.getFullYear() +
                "-" +
                S25String.leftPad(dateInput.getMonth() + 1, 2) +
                "-" +
                S25String.leftPad(dateInput.getDate(), 2) +
                "T" +
                S25String.leftPad(dateInput.getHours(), 2) +
                ":" +
                S25String.leftPad(dateInput.getMinutes(), 2) +
                ":" +
                S25String.leftPad(dateInput.getSeconds(), 2);
        } else {
            dateStr = dateInput + "";
        }

        //strip UTC tz
        dateStr = dateStr.replace("Z", "");

        //strip past offset
        let parts: string[] = dateStr.split("-");
        if (parts.length >= 4) {
            //eg, 2015-08-18T11:15:00-04:00
            dateStr = parts[0] + "-" + parts[1] + "-" + parts[2];
        } else {
            //strip future offset
            parts = dateStr.split("+");
            if (parts.length >= 2) {
                dateStr = parts[0];
            }
        }

        // S25Timezone._STARTOF_FORMAT.millisecond — inlined to avoid a S25DateParse <-> S25Timezone cycle
        return window.dayjs(dateStr, "YYYY-MM-DD HH:mm:ss.SSS").toDate();
    }

    /** parseDropTZ for ISO strings; anything else (e.g. an already-formatted label) parses loosely. */
    export function parseDropTZLenient(rawDate: string | Date): Date;
    export function parseDropTZLenient(rawDate: string | Date | undefined | null): Date | undefined;
    export function parseDropTZLenient(rawDate: string | Date | undefined | null) {
        if (typeof rawDate === "string" && /^\d{4}-\d{2}-\d{2}/.test(rawDate)) return parseDropTZ(rawDate);
        return date(rawDate);
    }

    export function time(timeStr: string | Date, dropTZ?: boolean): Date {
        const parser = dropTZ ? parseDropTZ : S25DateParse;

        // string - assume time-only string, so prepend with today's date (regardless of tzoffset)
        if (typeof timeStr === "string") {
            return parser(window.dayjs().format("YYYY-MM-DD") + " " + timeStr);
        }
        // Date object - replace Day with current Day, so the return value will be valid Date() object
        return parser(window.dayjs(timeStr).format("HH:mm:ss"));
    }

    export function parseFuzzyTimeString(timeString: string, useAllHours?: boolean, defaultNow?: boolean): Date | null {
        if (timeString === "") return null;

        const time = timeString.match(/(\d+)(:(\d\d))?\s*([ap]?)/i);
        if (time == null) return null;

        let hours = S25Number.parseInt(time[1]);
        let minutes = S25Number.parseInt(time[3]) || 0;

        //parse 24 hours in the form of 1400, 1530, 830, where no : is given
        if (hours >= 100 && timeString.indexOf(":") === -1) {
            minutes = hours % 100;
            minutes = minutes > 59 ? 59 : minutes;
            hours = Math.floor(hours / 100);
        }

        let isPM = false;

        if (!time[4]) {
            //unknown am/pm signal, make assumption based off hours, if useAllHours == true do not make assumption -implemented for pricing rate schedules -TW
            isPM = hours >= 12 || (!useAllHours && [1, 2, 3, 4, 5].indexOf(hours) > -1); //note noon is PM
        } else {
            isPM = time[4] === "p" || time[4] === "P";
        }

        if (hours < 12 && isPM) {
            //hours less than 12 and we are in PM, add 12 to get 24-hour time for hours
            hours += 12;
        } else if (hours === 12 && !isPM) {
            //hours is 12 but we are in AM, set hours to 0 to get proper 24-hour time for hours
            hours = 0;
        }

        const d = defaultNow ? new Date() : new Date(1970, 1, 1, 0, 0, 0);
        d.setHours(hours);
        d.setMinutes(minutes);
        d.setSeconds(0);
        d.setMilliseconds(0);
        return d;
    }
}
